"use client";
import React, { useEffect, useMemo, useState } from "react";
import { ScheduledOrderDropdown } from "../scheduledOrderDropdown/scheduledOrderDropdown";
import { SuccessBanner } from "../cartDrawer/cartStatusBanner";
import { UnavailableBanner } from "../cartDrawer/cartStatusBanner";
import { PrimaryButton } from "../button/primaryButton";
import CloseIcon from "@/components/_icons/closeIcon";
import { OrderType } from "@/types";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import { DeliveryEstimateBanner } from "../deliveryEstimateBanner/deliveryEstimateBanner";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import { GiftCardCartItem } from "@/app/(shop)/giftcard/components/giftCardCartItem";
import { GiftCardCartOrdersBanner } from "@/app/(shop)/giftcard/components/giftCardCartOrdersBanner";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { ScheduledTime } from "@/dto/scheduledTime";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { TrashIcon } from "../_icons/trashIcon";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { RetailCartItem } from "@/dto/retailCartItem";
import { useRouter } from "next13-progressbar";
import { useCurrency } from "@/app/providers/currencyContext";
import { useTranslations } from "next-intl";
import useMediaQuery from "@/utils/useMediaQuery";
import ArrowRightIcon from "../_icons/arrowRightIcon";
import { CartMerchantInfo } from "../cartDrawer/cartMerchantInfo";
import { isEmpty } from "lodash";
import { InactiveMenuCartOrdersBanner } from "@/app/(shop)/giftcard/components/InactiveMenuCartOrdersBanner";
import { getCartItemsStatus, removeHashFromURL } from "@/utils/cartUtils";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import { usePathname, useSearchParams } from "next/navigation";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { DayPart } from "@/dto/dayPartMenu";
import { canMerchantAcceptOrders } from "@/utils/scheduleOrderUtils";
import { getMenuDisplayName } from "@/utils/dayPartMenuUtils";

export const RestaurantCartItemView = ({
  onClose,
  merchant,
  hash,
  cartItems,
  orderType,
  setOrderType,
  scheduledOrder,
  setScheduledOrder,
  setShowScheduledOrderForm,
  removeFromCart,
  checkoutDisabled,
  merchantColor,
  handleContinueToCheckout,
  setIsRestaurantCartOpen,
  giftCard,
  setGiftCard,
  selectedDayPart = null,
}: { orderType: OrderType } & any) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const menuName = searchParams?.get("menu");
  const storedSchedule = getFromSessionStorage<Partial<ScheduledTime>>(
    SessionStorageKey.SCHEDULED_TIME,
    {}
  );
  const [unavailableItemsCriteria, setUnavailableItemsCriteria] = useState<
    "past" | "inactive" | null
  >(null);
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  const hasItemsInCart = cartItems.length > 0;
  const handleItemClick = (item: RetailCartItem) => {
    const selectedItem = item.value;
    const queryParams = new URLSearchParams();
    queryParams.set("menu", menuName || "");
    router.push(`/menu/${getItemUrl(selectedItem)}/?${queryParams.toString()}`);
    onClose();
  };
  const { format } = useCurrency();
  const t = useTranslations("RestaurantCartItemView");
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    if (isEmpty(cartItems) || !merchant?.isRestaurant) return;

    /**
     * When cart has items and hash has value that means the items are added from order again scenario
     * In this case check if merchant accepting orders (ASAP or scheduling)
     * If current time is outside business hours or in menu gap and merchant cannot accept order scheduling
     * based on business hours/chosen menu times then show a message and disable checkout
     */
    const merchantAcceptingOrders = canMerchantAcceptOrders(
      merchant,
      false,
      selectedDayPart
    );

    if (!merchantAcceptingOrders) setUnavailableItemsCriteria("inactive");
  }, [cartItems, merchant, selectedDayPart]);

  const handleCheckoutClick = () => {
    if (!isEmpty(selectedDayPart)) {
      const cartItemsStatus = getCartItemsStatus(
        cartItems,
        storedSchedule.asap ?? false,
        selectedDayPart,
        merchant.timezone
      );

      if (cartItemsStatus !== "active") {
        setUnavailableItemsCriteria(cartItemsStatus);
        return;
      }
    }

    handleContinueToCheckout();
  };

  const getInactiveItemsMessage = (
    dayPart: DayPart,
    criteria: "past" | "inactive",
    timezone: string
  ): string => {
    switch (criteria) {
      case "inactive":
        return t("invalidItems", {
          itemsText: giftCard ? t("nonGiftCardItems") : t("items"),
        });
      case "past":
        if (!isEmpty(dayPart)) {
          const currentMenuName = getMenuDisplayName(dayPart?.menuName, t);
          const time = HHMMtoDate(
            dayPart?.time?.endTime.toString().padStart(4, "0"),
            timezone,
            new Date()
          ).toLocaleString({
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });

          return `${t("dayPartMenuMessage", {
            menuName: currentMenuName,
            time,
          })} ${giftCard ? t("giftCardMessage") : ""} ${t("switchMenu")}`;
        }
        return "";
      default:
        return "";
    }
  };

  const onlyGiftCardInCart = !hasItemsInCart && giftCard;

  return (
    <>
      <div className="flex flex-row justify-between items-start px-4 pt-[15px] pb-[13px] sm:pb-0 sm:pt-8 shadow-cartHeader sm:shadow-none">
        <div>
          <h2 className="text-xl font-medium sm:text-2xl sm:font-bold">
            {t("yourCart", {
              count:
                cartItems.reduce(
                  (adder: any, item: any) => adder + item.value?.quantity,
                  0
                ) + (giftCard ? 1 : 0),
            })}
          </h2>
        </div>

        <button
          onClick={() => {
            removeHashFromURL(hash, pathName!, searchParams!);
            onClose();
          }}
          className="text-gray-600 hover:text-gray-900 mr-4"
        >
          <CloseIcon />
        </button>
      </div>
      {isMobileScreen && <CartMerchantInfo merchant={merchant} />}
      {hasItemsInCart && (
        <div className="px-4 pb-[3px] pt-[25px] sm:pb-4 flex flex-row justify-between items-center gap-4 sm:pt-8">
          <OrderTypeDropdown
            orderType={orderType}
            setOrderType={setOrderType}
            availableOrderTypes={merchant.services}
          />
          <ScheduledOrderDropdown
            scheduledOrder={scheduledOrder}
            setScheduledOrder={setScheduledOrder}
            showScheduleScreen={() => setShowScheduledOrderForm(true)}
            leadTime={merchant?.leadTime}
            merchant={merchant}
            orderType={orderType}
            selectedDayPart={selectedDayPart}
          />
        </div>
      )}
      {hash && hash === "#restaurant-cart" && cartItems.length > 0 && (
        <SuccessBanner count={cartItems.length} />
      )}
      {hash && hash === "#restaurant-cart-unavailable" && (
        <UnavailableBanner
          count={cartItems.length}
          message={
            !isEmpty(selectedDayPart)
              ? cartItems.length > 0
                ? t("partialItemsAddedToCart", {
                    count: cartItems.length,
                    menuName: getMenuDisplayName(selectedDayPart?.menuName, t),
                  })
                : t("allItemsUnavailable", {
                    menuName: getMenuDisplayName(selectedDayPart?.menuName, t),
                  })
              : ""
          }
        />
      )}

      <div className="flex-grow overflow-y-auto p-4">
        {storedSchedule.orderType === OrderType.DELIVERY && hasItemsInCart && (
          <DeliveryEstimateBanner
            merchant={merchant}
            onAddMore={setIsRestaurantCartOpen}
          />
        )}

        {!businessOpen && hasItemsInCart && giftCard && (
          <GiftCardCartOrdersBanner />
        )}

        {cartItems.length > 0 &&
        (unavailableItemsCriteria === "inactive" ||
          unavailableItemsCriteria === "past") ? (
          <InactiveMenuCartOrdersBanner
            message={getInactiveItemsMessage(
              selectedDayPart,
              unavailableItemsCriteria,
              merchant.timezone
            )}
          />
        ) : null}

        {cartItems.map((item: any, index: any) => (
          <div
            key={index}
            className="flex flex-col justify-between items-center py-4 border-b cursor-pointer"
            onClick={() => handleItemClick(item)}
          >
            <div className="w-full flex flex-row justify-between">
              <div className="w-full flex flex-col justify-between">
                <p className="text-lg text-[#373737] max-w-[310px]">
                  {item.value?.name}{" "}
                  {item.value?.quantity > 1 &&
                    t("quantityMultiplier", { count: item.value?.quantity })}
                </p>
                {item.value?.isAgeRestricted && <AgeRestricted />}
                <div className="max-w-[80%]">
                  {item.value?.modifiers?.length > 0 && (
                    <p className="text-[15px] text-[#727272]">
                      {modifierDisplay(item.value?.modifiers)}
                    </p>
                  )}
                  <div className="w-full">
                    <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                      {item.value?.specialInstructions}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-start items-end">
                <p className="text-[16px] font-medium mt-0.5">
                  {format(item.value?.total)}
                </p>
                <button
                  onClick={(event: React.MouseEvent) =>
                    removeFromCart(event, index)
                  }
                  className="mt-2"
                >
                  <TrashIcon width={14} height={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {giftCard && (
          <GiftCardCartItem
            giftCard={giftCard}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
              setGiftCard(null);
            }}
            onRequestClose={onClose}
            showBottomBorder={true}
          />
        )}
      </div>
      <div className="sm:p-4 sm:pb-8">
        <PrimaryButton
          style={{
            backgroundColor:
              (checkoutDisabled ||
                unavailableItemsCriteria === "inactive" ||
                unavailableItemsCriteria === "past") &&
              !onlyGiftCardInCart
                ? "#C7C7C7"
                : merchantColor,
          }}
          className="bg-black text-white font-bold py-2 rounded-none sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center px-[18px]"
          onClick={handleCheckoutClick}
          disabled={
            (checkoutDisabled ||
              unavailableItemsCriteria === "inactive" ||
              unavailableItemsCriteria === "past") &&
            !onlyGiftCardInCart
          }
        >
          <p className="text-xl font-medium">{t("continueToCheckout")}</p>
          {isMobileScreen && (
            <ArrowRightIcon
              className="sm:hidden"
              color="white"
              width={24}
              height={24.7}
            />
          )}
        </PrimaryButton>
      </div>
    </>
  );
};
